import React, { useState } from "react";

// --- Data & Content ---

const FAQS = [
    {
        q: "Do you build new stores from scratch?",
        a: "No. We partner with existing Australian merchants who have a store live but feel it isn't 'working' operationally. We fix the engine, we don't paint the car."
    },
    {
        q: "Do you handle Australian tax (GST) settings?",
        a: "Yes. We configure Shopify to correctly handle GST, tax invoices, and display prices according to Australian Consumer Law. We are not accountants, but we ensure the platform settings match your requirements."
    },
    {
        q: "Can you set up Australia Post and 3PL shipping?",
        a: "Absolutely. We configure weight-based zones, carrier-calculated rates for AusPost, and free shipping thresholds that protect your margins to regional WA/NT."
    },
    {
        q: "Do you work with Afterpay and Zip?",
        a: "Yes. We integrate all major Australian payment gateways, tune the fraud settings, and ensure the checkout flow is frictionless."
    },
    {
        q: "What access do you need?",
        a: "We request a collaborator account through your Shopify Partner dashboard. We never ask for your personal login password."
    }
];

const PACKAGES = [
    {
        title: "Launch Optimisation",
        price: "AUD $1,450",
        desc: "For new Australian stores needing a compliant, payment-ready foundation.",
        features: [
            "GST & Tax Invoice Setup",
            "Shopify Payments + Afterpay Activation",
            "Australia Post Shipping Zones (Standard + Express)",
            "Checkout Essentials (Shop Pay, key settings cleaned up)",
            "Branded Order Emails (logo, sender, reply-to fixed)",
            "Domain Connected & SSL Verified"
        ],
        cta: "Secure My Foundation",
        highlight: false
    },
    {
        title: "Growth Systems",
        price: "AUD $2,200",
        desc: "A 1–2 day optimisation sprint to remove operational friction and improve profit clarity.",
        features: [
            "Everything in Launch",
            "Profit Visibility Setup (Top 20 SKUs)",
            "Bundle & Offer Setup (Up to 2 offers)",
            "Free Shipping Threshold Setup + Cart Messaging",
            "Payments & Fraud Settings Tuned",
            "Pixel Events Verified (GA4 + Meta or TikTok)",
            "Cart & Checkout Quick-Wins (Top 5)"
        ],
        cta: "Optimise My Operations",
        highlight: true
    },
    {
        title: "Advanced Integrations",
        price: "Custom",
        desc: "For high-volume merchants with complex logistics.",
        features: [
            "Everything in Growth",
            "ERP / Inventory Sync (Cin7, Dear)",
            "3PL Integration (eStore, etc)",
            "International Markets (NZ/US/UK)",
            "Custom Flow Automation",
            "Quarterly Health Checks"
        ],
        cta: "Talk to a Strategist",
        highlight: false
    }
];

// --- Sub-components (Reused for consistency) ---

function Section({
                     className,
                     children,
                     id
                 }: {
    className?: string;
    children: React.ReactNode;
    id?: string;
}) {
    return (
        <section
            id={id}
            className={`py-20 px-6 md:px-12 max-w-7xl mx-auto ${className || ""}`}
        >
            {children}
        </section>
    );
}

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-100 text-sage-600 text-xs font-semibold uppercase tracking-wider mb-6">
            {children}
        </div>
    );
}

function CheckItem({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-1 flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <span className="text-slate-600 text-sm leading-relaxed">{children}</span>
        </div>
    );
}

// --- Main Application ---

export default function Landing() {
    const [openFaq, setOpenFaq] = useState<number | null>(0);

    return (
        <div className="bg-[#FBFBFD] text-slate-600 font-sans selection:bg-sage-100 selection:text-sage-900">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 font-display font-bold text-lg text-slate-900">
                        <div className="w-6 h-6 bg-sage-600 rounded-md"></div>
                        Kaagati
                        <span className="hidden sm:inline-block text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-medium">
              AU
            </span>
                    </div>
                    <div className="hidden md:flex gap-8 text-sm font-medium">
                        <a href="#problems" className="hover:text-slate-900 transition">
                            Pain Points
                        </a>
                        <a href="#approach" className="hover:text-slate-900 transition">
                            Our Approach
                        </a>
                        <a href="#pricing" className="hover:text-slate-900 transition">
                            Packages
                        </a>
                    </div>
                    <a
                        href="#contact"
                        className="bg-slate-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-slate-800 transition shadow-sm"
                    >
                        Request Free Review
                    </a>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="pt-24 pb-32 px-6 text-center max-w-4xl mx-auto">
                <Badge>Shopify Operations & Integration</Badge>
                <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-8 leading-[1.05] tracking-tight">
                    We make Australian stores <br className="hidden md:block" />
                    <span className="text-sage-600">operationally mature.</span>
                </h1>
                <p className="text-xl text-slate-500 leading-relaxed mb-10 max-w-2xl mx-auto">
                    Most stores are visually complete but structurally incomplete. We configure your GST, shipping zones,
                    margins, and workflows so you can scale without chaos.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                        href="#contact"
                        className="w-full sm:w-auto px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition shadow-lg shadow-sage-200/50"
                    >
                        Prioritise My Systems
                    </a>
                    <a
                        href="#pricing"
                        className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 border border-slate-200 rounded-full font-medium hover:bg-slate-50 transition"
                    >
                        View Packages
                    </a>
                </div>
            </header>

            {/* Problem Section - Australian Context */}
            <Section id="problems" className="bg-white rounded-[3rem] my-12 shadow-sm border border-slate-100">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 leading-tight">
                        You have a theme.
                        <br />
                        <span className="text-sage-600">Do you have a system?</span>
                    </h2>
                    <p className="text-slate-500 max-w-2xl mx-auto text-lg leading-relaxed">
                        Operational friction is the silent killer of Australian ecommerce brands.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {/* Problem 1 - GST */}
                    <div className="group p-8 rounded-3xl border border-slate-200 bg-white hover:border-sage-300 hover:shadow-xl hover:shadow-sage-100/50 transition duration-300 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                            <svg className="w-24 h-24 text-sage-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 14l6-6-6-6v12z" />
                            </svg>
                        </div>
                        <div className="w-14 h-14 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-6 text-2xl border border-red-100 group-hover:scale-110 transition">
                            🧾
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-4">The GST Headache</h3>
                        <p className="text-slate-500 leading-relaxed">
                            Is your checkout showing prices correctly? Are your tax invoices compliant for B2B customers?
                        </p>
                    </div>

                    {/* Problem 2 - Shipping */}
                    <div className="group p-8 rounded-3xl border border-slate-200 bg-white hover:border-sage-300 hover:shadow-xl hover:shadow-sage-100/50 transition duration-300 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                            <svg className="w-24 h-24 text-sage-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div className="w-14 h-14 bg-yellow-50 text-yellow-600 rounded-2xl flex items-center justify-center mb-6 text-2xl border border-yellow-100 group-hover:scale-110 transition">
                            🚚
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-4">Shipping & Zones</h3>
                        <p className="text-slate-500 leading-relaxed">
                            Are you losing money shipping to regional WA? Is your free shipping threshold actually protecting your
                            margin?
                        </p>
                    </div>

                    {/* Problem 3 - Payments */}
                    <div className="group p-8 rounded-3xl border border-slate-200 bg-white hover:border-sage-300 hover:shadow-xl hover:shadow-sage-100/50 transition duration-300 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                            <svg className="w-24 h-24 text-sage-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="w-14 h-14 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mb-6 text-2xl border border-blue-100 group-hover:scale-110 transition">
                            💳
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-4">Payment Friction</h3>
                        <p className="text-slate-500 leading-relaxed">
                            Are false declines blocked? Is Afterpay set up correctly? Are transaction fees accounted for?
                        </p>
                    </div>
                </div>
            </Section>

            {/* Comparison Section - Strategic Positioning */}
            <Section className="py-24 bg-white">
                <div className="text-center mb-16">
                    <Badge>Why Choose Us</Badge>
                    <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">The "Middle" is where you win.</h2>
                    <p className="text-slate-500 max-w-2xl mx-auto text-lg">
                        We sit perfectly between the chaos of a freelancer and the bloat of an agency.
                    </p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] border-collapse">
                        <thead>
                        <tr className="border-b border-slate-100">
                            <th className="py-6 px-4 text-left text-sm font-semibold text-slate-400 uppercase tracking-wider w-1/4">
                                Feature
                            </th>
                            <th className="py-6 px-4 text-left text-lg font-bold text-slate-400 w-1/4">Freelancer</th>
                            <th className="py-6 px-4 text-left text-lg font-bold text-slate-400 w-1/4">Big Agency</th>
                            <th className="py-6 px-4 text-left text-xl font-bold text-sage-600 bg-sage-50/50 rounded-t-2xl w-1/4 border-t-4 border-sage-500">
                                Kaagati Systems
                            </th>
                        </tr>
                        </thead>
                        <tbody className="text-slate-600">
                        <tr className="border-b border-slate-50 group hover:bg-slate-50/50 transition">
                            <td className="py-6 px-4 font-semibold text-slate-900">Pricing Structure</td>
                            <td className="py-6 px-4">Hourly (Unpredictable)</td>
                            <td className="py-6 px-4">$10k+ Retainers</td>
                            <td className="py-6 px-4 font-bold text-slate-900 bg-sage-50/30 border-l-4 border-sage-500">
                                Fixed Project Fee
                            </td>
                        </tr>
                        <tr className="border-b border-slate-50 group hover:bg-slate-50/50 transition">
                            <td className="py-6 px-4 font-semibold text-slate-900">Speed to Value</td>
                            <td className="py-6 px-4">Weeks / Months</td>
                            <td className="py-6 px-4">3-6 Month Onboarding</td>
                            <td className="py-6 px-4 font-bold text-slate-900 bg-sage-50/30 border-l-4 border-sage-500">
                                48-Hour Systems Review
                            </td>
                        </tr>
                        <tr className="border-b border-slate-50 group hover:bg-slate-50/50 transition">
                            <td className="py-6 px-4 font-semibold text-slate-900">Focus Area</td>
                            <td className="py-6 px-4">"I do everything"</td>
                            <td className="py-6 px-4">Brand & Creative</td>
                            <td className="py-6 px-4 font-bold text-slate-900 bg-sage-50/30 border-l-4 border-sage-500">
                                Operations Only
                            </td>
                        </tr>
                        <tr className="border-b border-slate-50 group hover:bg-slate-50/50 transition">
                            <td className="py-6 px-4 font-semibold text-slate-900">Deliverable</td>
                            <td className="py-6 px-4">Code Snippets</td>
                            <td className="py-6 px-4">Slide Decks</td>
                            <td className="py-6 px-4 font-bold text-slate-900 bg-sage-50/30 border-l-4 border-sage-500 rounded-b-2xl">
                                Working System
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Our Approach */}
            <Section id="approach">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <Badge>The "OS" Philosophy</Badge>
                        <h2 className="text-4xl font-bold mb-6">
                            Visuals attract customers.
                            <br />
                            Systems keep them.
                        </h2>
                        <p className="text-lg text-slate-500 mb-8">
                            We don't touch your logo or your banner images. We work under the hood, configuring the "Operating System"
                            of your store.
                        </p>
                        <div className="space-y-6">
                            {[
                                { label: "Pricing", text: "Margin logic, bundles, and discount hierarchies." },
                                { label: "Logistics", text: "Australia Post rates, zones, and 3PL connections." },
                                { label: "Tracking", text: "GA4 and Pixel events that actually match bank deposits." }
                            ].map((p) => (
                                <div key={p.label} className="flex gap-4">
                                    <div className="w-24 shrink-0 font-bold text-slate-900">{p.label}</div>
                                    <div className="text-slate-500">{p.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="relative bg-[#F4F5F4] rounded-3xl p-12 aspect-square flex items-center justify-center">
                        {/* Abstract Representation of Order System */}
                        <div className="w-full max-w-sm space-y-3">
                            <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm opacity-60">
                                <div className="h-2 w-24 bg-slate-200 rounded"></div>
                                <div className="h-2 w-8 bg-slate-200 rounded"></div>
                            </div>
                            <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-md border-l-4 border-sage-600 scale-105">
                                <div className="space-y-2">
                                    <div className="h-3 w-32 bg-slate-800 rounded"></div>
                                    <div className="h-2 w-20 bg-slate-400 rounded"></div>
                                </div>
                                <div className="h-8 w-8 rounded-full bg-sage-100 flex items-center justify-center text-sage-600">
                                    ✓
                                </div>
                            </div>
                            <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm opacity-60">
                                <div className="h-2 w-28 bg-slate-200 rounded"></div>
                                <div className="h-2 w-8 bg-slate-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Process Section - Horizontal Timeline */}
            <Section id="process" className="bg-slate-900 text-white rounded-[3rem] my-24 overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>

                <div className="relative z-10 text-center mb-20">
                    <Badge>How We Work</Badge>
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Structured execution.</h2>
                    <p className="text-slate-400 max-w-2xl mx-auto text-lg">
                        We respect your live environment. Our 4-step protocol ensures zero downtime and maximum clarity.
                    </p>
                </div>

                <div className="relative z-10 max-w-6xl mx-auto">
                    {/* Desktop Connecting Line */}
                    <div className="hidden md:block absolute top-[2.5rem] left-[10%] right-[10%] h-0.5 bg-slate-800"></div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {[
                            {
                                step: "01",
                                title: "Assess",
                                desc: "We scan tax, shipping, payments & tracking.",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                )
                            },
                            {
                                step: "02",
                                title: "Prioritise",
                                desc: "We lock a clear sequence of fixes.",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                )
                            },
                            {
                                step: "03",
                                title: "Configure",
                                desc: "We implement safely with zero downtime.",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                )
                            },
                            {
                                step: "04",
                                title: "Handover",
                                desc: "Video SOPs & settings summary for your team.",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                )
                            }
                        ].map((s, i) => (
                            <div key={i} className="relative group text-center md:text-left">
                                {/* Number Bubble */}
                                <div className="w-20 h-20 mx-auto md:mx-0 bg-slate-900 border-4 border-slate-800 rounded-full flex items-center justify-center text-xl font-bold text-white relative z-10 mb-6 group-hover:border-sage-500 group-hover:bg-sage-600 transition duration-300">
                                    {s.step}
                                </div>

                                {/* Content Card */}
                                <div className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition">
                                    <div className="flex items-center gap-3 mb-3 text-sage-400">
                                        {s.icon}
                                        <h3 className="text-lg font-bold text-white">{s.title}</h3>
                                    </div>
                                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Pricing Packages */}
            <Section id="pricing">
                <div className="text-center mb-16">
                    <Badge>Transparent Investment</Badge>
                    <h2 className="text-4xl font-bold mb-4">Flat fees. No surprises.</h2>
                    <p className="text-slate-500">Choose the level of operational maturity you need.</p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {PACKAGES.map((pkg) => (
                        <div
                            key={pkg.title}
                            className={`relative p-8 rounded-3xl border ${
                                pkg.highlight ? "border-sage-500 bg-white ring-4 ring-sage-50" : "border-slate-200 bg-white"
                            }`}
                        >
                            {pkg.highlight && (
                                <div className="absolute top-0 right-0 bg-sage-600 text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-2xl">
                                    GROWTH STANDARD
                                </div>
                            )}
                            <h3 className="text-xl font-bold text-slate-900">{pkg.title}</h3>
                            <div className="text-3xl font-bold text-slate-900 mt-4 mb-2">{pkg.price}</div>
                            <p className="text-sm text-slate-500 min-h-[40px] mb-8">{pkg.desc}</p>

                            <div className="space-y-4 mb-8">
                                {pkg.features.map((f) => (
                                    <CheckItem key={f}>{f}</CheckItem>
                                ))}
                            </div>

                            <a
                                href="#contact"
                                className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition ${
                                    pkg.highlight ? "bg-sage-600 text-white hover:bg-sage-700" : "bg-slate-100 text-slate-900 hover:bg-slate-200"
                                }`}
                            >
                                {pkg.cta}
                            </a>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Deliverables */}
            <Section className="bg-sky-50 rounded-[3rem]">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-3xl font-bold mb-6">What you walk away with.</h2>
                        <p className="text-slate-600 mb-8">You don't just get settings changed. You get a documented system.</p>
                        <ul className="space-y-4">
                            <CheckItem>Operational Architecture Diagram</CheckItem>
                            <CheckItem>SOPs for Ongoing Management</CheckItem>
                            <CheckItem>Loom Video Walkthroughs</CheckItem>
                            <CheckItem>30 Days of Priority Support</CheckItem>
                        </ul>
                    </div>
                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 -rotate-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-600 font-bold">
                                ✓
                            </div>
                            <div>
                                <div className="font-bold text-slate-900">System Handover</div>
                                <div className="text-xs text-slate-500">Completed 12:45 PM</div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {["Config_Log.pdf", "Tax_Settings_Final.pdf", "Shipping_Zones_Map.png"].map((f) => (
                                <div key={f} className="flex items-center gap-3 p-3 bg-slate-50 rounded border border-slate-100">
                                    <div className="text-lg">📄</div>
                                    <div className="text-sm font-medium text-slate-700">{f}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section>
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">Common questions.</h2>
                    <div className="space-y-4">
                        {FAQS.map((obj, i) => (
                            <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between p-6 text-left font-bold text-slate-900 hover:bg-slate-50 transition"
                                >
                                    {obj.q}
                                    <span className={`transition-transform text-slate-400 ${openFaq === i ? "rotate-180" : ""}`}>↓</span>
                                </button>
                                {openFaq === i && (
                                    <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed">{obj.a}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Contact Form Section */}
            <Section id="contact" className="bg-sage-50 rounded-[3rem] my-24 relative overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-16 relative z-10">
                    <div>
                        <Badge>Request a Free Review</Badge>
                        <h2 className="text-4xl font-bold mb-6 text-slate-900">Let's stabilise your store.</h2>
                        <p className="text-lg text-slate-500 mb-8">
                            Fill out the form to request a 48-hour systems review. We’ll check your backend and send you a priority
                            roadmap—free.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-sage-600 shadow-sm">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <div className="font-bold text-slate-900">Email Us Directly</div>
                                    <a href="mailto:support@kaagati.com" className="text-sage-600 hover:underline">
                                        support@kaagati.com
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-sage-600 shadow-sm">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                        />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="font-bold text-slate-900">Based in Australia</div>
                                    <div className="text-slate-500">Serving merchants in Syd, Melb, Bris & Perth</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-3xl shadow-lg shadow-sage-900/5 border border-sage-100">
                        <ContactForm />
                    </div>
                </div>
            </Section>

            <footer className="py-12 text-center text-sm text-slate-400 border-t border-slate-100 bg-white">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>© 2021 Kaagati. All rights reserved.</div>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-slate-600 transition">
                            Privacy Policy
                        </a>
                        <a href="#" className="hover:text-slate-600 transition">
                            Terms of Service
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function ContactForm() {
    const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setStatus("submitting");

        const formData = new FormData(e.currentTarget);
        // Add your Web3Forms Access Key here. Get one for free at https://web3forms.com/
        formData.append("access_key", "cea25dfc-fefb-4444-adbc-813b7437e2c0");

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                setStatus("success");
            } else {
                setStatus("error");
            }
        } catch (err) {
            setStatus("error");
        }
    }

    if (status === "success") {
        return (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 text-2xl">
                    ✓
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Request Received</h3>
                <p className="text-slate-500">We'll be in touch within 24 hours to schedule your review.</p>
                <button onClick={() => setStatus("idle")} className="mt-8 text-sage-600 font-medium hover:underline">
                    Send another message
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input type="hidden" name="subject" value="New Systems Review Request from Kaagati Site" />

            <div>
                <label htmlFor="name" className="block text-sm font-bold text-slate-900 mb-1">
                    Your Name
                </label>
                <input
                    required
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Jane Doe"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                />
            </div>

            <div>
                <label htmlFor="email" className="block text-sm font-bold text-slate-900 mb-1">
                    Work Email
                </label>
                <input
                    required
                    type="email"
                    name="email"
                    id="email"
                    placeholder="jane@brand.com.au"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                />
            </div>

            <div>
                <label htmlFor="website" className="block text-sm font-bold text-slate-900 mb-1">
                    Store URL
                </label>
                <input
                    required
                    type="url"
                    name="website"
                    id="website"
                    placeholder="https://brand.com.au"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                />
            </div>

            <div>
                <label htmlFor="message" className="block text-sm font-bold text-slate-900 mb-1">
                    Biggest Operational Headache?
                </label>
                <textarea
                    required
                    name="message"
                    id="message"
                    rows={3}
                    placeholder="e.g. My shipping zones are messy..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                ></textarea>
            </div>

            <button
                disabled={status === "submitting"}
                type="submit"
                className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition shadow-lg disabled:opacity-70 disabled:cursor-not-allowed mt-4"
            >
                {status === "submitting" ? "Sending Request..." : "Request Free Review"}
            </button>

            {status === "error" && (
                <p className="text-red-500 text-sm text-center mt-2">Something went wrong. Please try again or email us directly.</p>
            )}

            <p className="text-xs text-center text-slate-400 mt-4">We respect your privacy. No spam, ever.</p>
        </form>
    );
}
